import { useState, useRef, useCallback, useEffect } from "react";
import {
  ExternalLink,
  MapPin,
  Check,
  Save,
  X,
  Globe,
  Ban,
  SkipForward,
  Phone,
} from "lucide-react";
import { useMap, InfoWindow } from "@vis.gl/react-google-maps";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import type { OwnershipType, StructuredClassificationData } from "@/types/domain";
import { GoogleMapWrapper } from "@/components/map/GoogleMapWrapper";
import { MapMarker } from "@/components/map/MapMarker";
import { SportCourtMarker } from "@/components/map/SportCourtMarker";
import { CourtPlacementToolbar } from "@/components/map/CourtPlacementToolbar";
import { StatusIndicator } from "@/components/locations/StatusIndicator";
import { PhotoCarousel } from "@/components/map/PhotoCarousel";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClassificationsSection } from "@/components/map/ClassificationsSection";
import { ClassificationForm } from "./ClassificationForm";
import { GoogleReviewsModal } from "./GoogleReviewsModal";

interface ClassificationRow {
  id: string;
  source: "satellite" | "website" | "web_research";
  has_sport_field: boolean | null;
  user_has_sport_field: boolean | null;
  user_notes?: string | null;
  confidence: number | null;
  notes: string | null;
  model_used: string | null;
  structured_data?: StructuredClassificationData | null;
}

interface ReviewPanelProps {
  locationId: string;
  collectionId?: string;
  onSaved: () => void;
  onRejected: () => void;
  onSkipped: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPrimaryClassification(classifications: ClassificationRow[]) {
  return (
    classifications.find((c) => c.source === "satellite") ??
    classifications[0] ??
    null
  );
}

function formatType(type: string): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Quick-Facts Card (aggregated from all classification sources)
// ---------------------------------------------------------------------------

function QuickFactsCard({ classifications }: { classifications: ClassificationRow[] }) {
  const allData: { source: string; data: StructuredClassificationData }[] = [];
  for (const c of classifications) {
    if (c.structured_data) {
      allData.push({ source: c.source, data: c.structured_data });
    }
  }

  if (allData.length === 0) return null;

  const sportMap = new Map<string, { type: string; certainty: "definite" | "possible"; sources: string[] }>();
  for (const { source, data } of allData) {
    for (const sport of data.sports) {
      const key = sport.type.toLowerCase();
      const existing = sportMap.get(key);
      if (existing) {
        if (sport.certainty === "definite") existing.certainty = "definite";
        if (!existing.sources.includes(source)) existing.sources.push(source);
      } else {
        sportMap.set(key, { type: sport.type, certainty: sport.certainty, sources: [source] });
      }
    }
  }

  const normalizeFlag = (text: string) => text.toLowerCase().trim().replace(/[.,;:!?]+$/, "");
  const keepFlags: string[] = [];
  const rejectFlags: string[] = [];
  const seenFlags = new Set<string>();
  for (const { data } of allData) {
    for (const flag of data.flags) {
      const key = normalizeFlag(flag.text);
      if (seenFlags.has(key)) continue;
      const isSubstring = Array.from(seenFlags).some(
        (existing) => existing.includes(key) || key.includes(existing),
      );
      if (isSubstring) continue;
      seenFlags.add(key);
      if (flag.type === "keep") keepFlags.push(flag.text);
      else rejectFlags.push(flag.text);
    }
  }

  const sports = Array.from(sportMap.values());

  if (sports.length === 0 && keepFlags.length === 0 && rejectFlags.length === 0) return null;

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Quick Facts
      </span>

      {sports.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {sports.map((sport) => (
            <span
              key={sport.type}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                sport.certainty === "definite"
                  ? "bg-violet-100 text-violet-800 border border-violet-300"
                  : "bg-violet-50 text-violet-600 border border-dashed border-violet-300"
              }`}
            >
              {formatType(sport.type.replace(/^other:/, ""))}
              {sport.certainty === "possible" && (
                <span className="ml-1 text-[10px] opacity-60">?</span>
              )}
            </span>
          ))}
        </div>
      )}

      {keepFlags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {keepFlags.map((text) => (
            <span
              key={text}
              className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700"
            >
              <Check className="size-3" />
              {text}
            </span>
          ))}
        </div>
      )}

      {rejectFlags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {rejectFlags.map((text) => (
            <span
              key={text}
              className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-xs font-medium text-red-700"
            >
              <X className="size-3" />
              {text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Map content — rendered inside GoogleMapWrapper so useMap() works
// ---------------------------------------------------------------------------

interface ReviewMapContentProps {
  location: { id: string; lat: number; lng: number; name: string; primary_type: string | null; ownership: string | null; is_reviewed?: boolean | null; is_rejected?: boolean | null };
  hasSportField: boolean | null;
  isPlacing: boolean;
  armedSportType: string | null;
  onCourtCreated: () => void;
  pendingCoords: { lat: number; lng: number } | null;
  onPinDragEnd: (lat: number, lng: number) => void;
  courtPins: any[] | undefined;
  collectionId?: string;
}

function ReviewMapContent({ location, hasSportField, isPlacing, armedSportType, onCourtCreated, pendingCoords, onPinDragEnd, courtPins, collectionId }: ReviewMapContentProps) {
  const map = useMap();
  const utils = trpc.useUtils();

  const createMutation = trpc.sportCourts.create.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId: location.id });
      onCourtCreated();
    },
  });

  const updateMutation = trpc.sportCourts.update.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId: location.id });
    },
  });

  const deleteMutation = trpc.sportCourts.delete.useMutation({
    onSuccess: () => {
      utils.sportCourts.listByLocation.invalidate({ locationId: location.id });
    },
  });

  // Click-to-place handler
  useEffect(() => {
    if (!map || !isPlacing || !armedSportType) return;

    const listener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        createMutation.mutate({
          locationId: location.id,
          sportType: armedSportType,
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        });
      }
    });

    return () => listener.remove();
  }, [map, isPlacing, armedSportType, location.id, createMutation]);

  // Change cursor while placing
  useEffect(() => {
    if (!map) return;
    if (isPlacing && armedSportType) {
      map.setOptions({ draggableCursor: "crosshair" });
    } else {
      map.setOptions({ draggableCursor: "" });
    }
    return () => { map.setOptions({ draggableCursor: "" }); };
  }, [map, isPlacing, armedSportType]);

  const handleDragEnd = useCallback((courtId: string) => (lat: number, lng: number) => {
    updateMutation.mutate({ id: courtId, lat, lng });
  }, [updateMutation]);

  const handleCourtClick = useCallback((courtId: string) => {
    if (isPlacing) {
      deleteMutation.mutate({ id: courtId });
    }
  }, [isPlacing, deleteMutation]);

  // All locations (or collection-filtered) for context on the review map
  const { data: allMapLocations } = trpc.locations.getMapData.useQuery(
    { collectionIds: collectionId ? [collectionId] : undefined },
    { staleTime: 60_000 },
  );
  // Exclude current location from the context markers
  const nearbyLocations = allMapLocations?.filter((l: any) => l.id !== location.id);

  const [hoveredNearby, setHoveredNearby] = useState<string | null>(null);

  // Dismiss tooltip on map click
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", () => setHoveredNearby(null));
    return () => listener.remove();
  }, [map]);
  const nearbyUtils = trpc.useUtils();
  const markDupeMutation = trpc.locations.markDuplicate.useMutation({
    onSuccess: () => {
      nearbyUtils.locations.getMapData.invalidate();
      setHoveredNearby(null);
      toast.success("Marked as duplicate");
    },
    onError: (err) => {
      toast.error(`Failed: ${err.message}`);
    },
  });

  return (
    <>
      {/* Nearby context markers */}
      {nearbyLocations?.map((nearby: any) => (
        <MapMarker
          key={nearby.id}
          locationId={nearby.id}
          position={{ lat: nearby.lat, lng: nearby.lng }}
          hasSportField={null}
          primaryType={nearby.primary_type}
          name={nearby.name}
          ownership={nearby.ownership ?? undefined}
          source={nearby.source ?? undefined}
          isMuted={false}
          isReviewed={nearby.is_reviewed ?? false}
          isRejected={nearby.is_rejected ?? false}
          onClick={() => setHoveredNearby(hoveredNearby === nearby.id ? null : nearby.id)}
        />
      ))}

      {/* InfoWindow tooltip for clicked nearby marker */}
      {hoveredNearby && nearbyLocations?.find((n: any) => n.id === hoveredNearby) && (() => {
        const n: any = nearbyLocations.find((n: any) => n.id === hoveredNearby)!;
        const isDupe = n.is_rejected && !!n.duplicate_of;
        const label = n.is_reviewed ? "Reviewed" : isDupe ? "Duplicate" : n.is_rejected ? "Rejected" : "Unreviewed";
        const cls = n.is_reviewed ? "bg-green-100 text-green-700" : isDupe ? "bg-orange-100 text-orange-700" : n.is_rejected ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600";
        return (
          <InfoWindow
            position={{ lat: n.lat, lng: n.lng }}
            shouldFocus={false}
            headerDisabled
            pixelOffset={[0, -40] as any}
            onCloseClick={() => setHoveredNearby(null)}
          >
            <div className="space-y-2 min-w-[180px] max-w-[220px] p-3">
              <p className="text-sm font-medium">{n.name}</p>
              {n.address && <p className="text-xs text-gray-500 truncate">{n.address}</p>}
              <div className="flex gap-1.5 text-xs flex-wrap">
                <span className={`px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
                {n.ownership && n.ownership !== "unknown" && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{n.ownership}</span>
                )}
              </div>
              <button
                className="w-full text-xs px-2 py-1.5 rounded bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50"
                disabled={markDupeMutation.isPending}
                onClick={() => markDupeMutation.mutate({ locationId: n.id, duplicateOf: location.id })}
              >
                {markDupeMutation.isPending ? "Marking..." : "Mark as Duplicate"}
              </button>
            </div>
          </InfoWindow>
        );
      })()}

      {/* Current location marker — highlighted with glow */}
      <MapMarker
        locationId={location.id}
        position={pendingCoords ?? { lat: location.lat, lng: location.lng }}
        hasSportField={hasSportField}
        primaryType={location.primary_type ?? null}
        name={location.name}
        ownership={location.ownership ?? undefined}
        isDraggable
        onDragEnd={onPinDragEnd}
        isMuted={false}
        isReviewed={location.is_reviewed ?? false}
        isRejected={location.is_rejected ?? false}
        isSelected
      />
      {courtPins?.map((court) => (
        <SportCourtMarker
          key={court.id}
          courtId={court.id}
          position={{ lat: court.lat, lng: court.lng }}
          sportType={court.sport_type}
          isDraggable={isPlacing}
          onDragEnd={handleDragEnd(court.id)}
          onClick={handleCourtClick}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ReviewPanel({
  locationId,
  collectionId,
  onSaved,
  onRejected,
  onSkipped,
}: ReviewPanelProps) {
  const { data: location, isLoading } = trpc.locations.getById.useQuery(
    { id: locationId },
    { enabled: !!locationId },
  );

  const saveMutation = trpc.locations.saveClassification.useMutation({
    onSuccess: () => onSaved(),
  });

  const rejectMutation = trpc.locations.setRejected.useMutation({
    onSuccess: () => onRejected(),
  });

  // Live-sync form overrides so status indicators update before saving
  const [formOverrides, setFormOverrides] = useState<{
    hasSportField: boolean | null;
    ownership: OwnershipType;
  } | null>(null);

  // Ref to read current form data for save action
  const formDataRef = useRef<{
    userHasSportField: boolean | null;
    userNotes: string | null;
    ownership: OwnershipType;
    visibilityRating: number | null;
    isCulturalSpot: boolean;
    approvedByCity: boolean;
    approvedBySponsor: boolean;
    ownershipConfirmed: boolean;
  } | null>(null);

  // Court placement mode
  const [isPlacing, setIsPlacing] = useState(false);
  const [armedSportType, setArmedSportType] = useState<string | null>("soccer_pitch");

  // Lifted courtPins query (for save-without-courts dialog)
  const { data: courtPins } = trpc.sportCourts.listByLocation.useQuery(
    { locationId },
    { enabled: !!locationId },
  );

  // Save-without-courts dialog state
  const [showNoCourtsDialog, setShowNoCourtsDialog] = useState(false);

  // Reviews modal state
  const [showReviewsModal, setShowReviewsModal] = useState(false);

  // Nearby duplicates query (150m radius for warning)
  const { data: nearbyDuplicates } = trpc.locations.nearby.useQuery(
    { lat: location?.lat ?? 0, lng: location?.lng ?? 0, radiusMeters: 150, excludeId: locationId, limit: 5 },
    { enabled: !!location },
  );
  const nonRejectedNearby = (nearbyDuplicates ?? []).filter((n: any) => !n.is_rejected);

  // Draggable pin state
  const [pendingCoords, setPendingCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Coordinate update mutation
  const updateCoordsMutation = trpc.locations.updateCoordinates.useMutation();

  // Reset overrides and placement mode when switching locations
  const [prevLocationId, setPrevLocationId] = useState(locationId);
  if (locationId !== prevLocationId) {
    setPrevLocationId(locationId);
    setFormOverrides(null);
    setIsPlacing(false);
    setPendingCoords(null);
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="aspect-video w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Location not found.
      </div>
    );
  }

  const classifications = (location.classifications ?? []) as unknown as ClassificationRow[];

  const hasSportField =
    classifications.find(
      (c) => c.source === "satellite",
    )?.has_sport_field ?? null;

  const primary = getPrimaryClassification(classifications);
  const webResearch = classifications.find(
    (c) => c.source === "web_research",
  );

  const displayOwnership = formOverrides?.ownership ?? (location.ownership as OwnershipType);
  const displayHasSportField = formOverrides?.hasSportField ?? primary?.has_sport_field ?? null;

  const doSave = () => {
    if (formDataRef.current) {
      saveMutation.mutate({
        locationId,
        ...formDataRef.current,
        markReviewed: true,
      });
      if (pendingCoords) {
        updateCoordsMutation.mutate({ locationId, ...pendingCoords });
      }
    }
  };

  const handleSave = () => {
    // Show confirmation if no courts placed and not rejecting
    if (!courtPins || courtPins.length === 0) {
      setShowNoCourtsDialog(true);
      return;
    }
    doSave();
  };

  const handleReject = () => {
    rejectMutation.mutate({ locationId, isRejected: true });
  };

  return (
    <div className="flex h-full">
      {/* Left: satellite map with court placement */}
      <div className="relative flex-1 border-r overflow-hidden">
        <GoogleMapWrapper
          key={locationId}
          defaultCenter={{ lat: location.lat, lng: location.lng }}
          defaultZoom={20}
          mapTypeId="hybrid"
          fullscreenControl={false}
        >
          <ReviewMapContent
            location={location}
            hasSportField={hasSportField}
            isPlacing={isPlacing}
            armedSportType={armedSportType}
            onCourtCreated={() => {}}
            pendingCoords={pendingCoords}
            onPinDragEnd={(lat, lng) => setPendingCoords({ lat, lng })}
            courtPins={courtPins}
            collectionId={collectionId}
          />
        </GoogleMapWrapper>

        {/* Court placement toolbar overlay */}
        {isPlacing && (
          <div className="absolute top-3 left-3 right-3 z-10 flex justify-center pointer-events-none">
            <div className="bg-background/95 backdrop-blur-sm border px-4 py-2.5 rounded-lg shadow-lg pointer-events-auto flex flex-col items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                {armedSportType ? "Click map to place · Click pin to delete" : "Select a sport type below"}
              </span>
              <CourtPlacementToolbar
                armedSportType={armedSportType}
                onArm={setArmedSportType}
                onDisarm={() => setArmedSportType(null)}
                onDone={() => setIsPlacing(false)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Right: sidebar matching LocationSidebarDetail pattern */}
      <div className="w-[28rem] shrink-0 flex flex-col h-full min-h-0">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto space-y-4 p-4">
            {/* Rejected banner */}
            {location.is_rejected && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                <Ban className="size-4 text-destructive shrink-0" />
                <span className="text-sm font-medium text-destructive">
                  This location has been rejected
                </span>
              </div>
            )}

            {/* Possible duplicate warning */}
            {nonRejectedNearby.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 space-y-1">
                <span className="text-sm font-medium text-amber-800">
                  Possible duplicate ({nonRejectedNearby.length} location{nonRejectedNearby.length !== 1 ? "s" : ""} within 150m)
                </span>
                <div className="space-y-0.5">
                  {nonRejectedNearby.map((n: any) => (
                    <div key={n.id} className="flex items-center gap-1.5 text-xs text-amber-700">
                      <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${n.is_reviewed ? "bg-green-500" : "bg-indigo-500"}`} />
                      <span className="truncate">{n.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo thumbnails */}
            {location.photo_refs?.length > 0 && (
              <PhotoCarousel photoRefs={location.photo_refs} />
            )}

            {/* Name */}
            <h2 className="text-lg font-semibold">{location.name}</h2>

            {/* Address */}
            {location.address && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Address
                </h3>
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="size-4 text-muted-foreground mt-0.5 shrink-0" />
                  <span>{location.address}</span>
                </div>
              </div>
            )}

            {/* Phone */}
            {location.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="size-4 text-muted-foreground shrink-0" />
                <a href={`tel:${location.phone}`} className="text-primary hover:underline">
                  {location.phone}
                </a>
              </div>
            )}

            {/* Status indicator pills */}
            <div className="flex gap-2">
              <StatusIndicator
                type="sport_field"
                hasSportField={displayHasSportField}
              />
              <StatusIndicator
                type="ownership"
                ownership={displayOwnership}
              />
              <StatusIndicator
                type="satellite_confidence"
                confidence={primary?.confidence ?? null}
              />
              <StatusIndicator
                type="web_confidence"
                confidence={webResearch?.confidence ?? null}
              />
              <StatusIndicator
                type="reviews"
                rating={location.rating}
                reviewCount={location.user_rating_count}
                onClick={() => setShowReviewsModal(true)}
              />
            </div>

            {/* Quick-Facts Card */}
            {classifications.length > 0 && (
              <QuickFactsCard classifications={classifications} />
            )}

            {/* Classifications */}
            {classifications.length > 0 && (
              <ClassificationsSection
                classifications={classifications as any}
                ownership={location.ownership}
                hasWebsite={!!location.website}
                isRejected={location.is_rejected}
              />
            )}

            {/* Place Courts toggle */}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              style={{ backgroundColor: "#f97316", color: "white", borderColor: "#f97316" }}
              onClick={() => setIsPlacing((v) => !v)}
            >
              <MapPin className="size-3.5 mr-1.5" />
              {isPlacing ? "Done Placing" : "Place Courts"}
            </Button>

            {/* Classification form (no action buttons) */}
            <ClassificationForm
              locationId={locationId}
              classifications={classifications as any}
              ownership={location.ownership as OwnershipType}
              userNotes={location.user_notes}
              visibilityRating={location.visibility_rating ?? null}
              isCulturalSpot={location.is_cultural_spot ?? false}
              approvedByCity={location.approved_by_city ?? false}
              approvedBySponsor={location.approved_by_sponsor ?? false}
              ownershipConfirmed={location.ownership_confirmed ?? false}
              isRejected={location.is_rejected}
              hideActions
              onChange={setFormOverrides}
              onFormDataChange={(data) => {
                formDataRef.current = data;
              }}
              onSave={() => {}}
              onReject={() => {
                rejectMutation.mutate({ locationId, isRejected: true });
              }}
              onUnreject={() => {
                rejectMutation.mutate({ locationId, isRejected: false });
              }}
              isSaving={saveMutation.isPending}
              isRejecting={rejectMutation.isPending}
            />

            {saveMutation.isSuccess && (
              <p className="text-sm text-green-600 mt-2">Classification saved.</p>
            )}
            {saveMutation.isError && (
              <p className="text-sm text-destructive mt-2">
                Failed: {saveMutation.error.message}
              </p>
            )}
        </div>

        {/* Pin moved indicator */}
        {pendingCoords && (
          <div className="px-4 py-1.5 bg-amber-50 border-t border-amber-200 text-xs text-amber-700">
            Pin moved — new position will be saved
          </div>
        )}

        {/* Sticky bottom action bar */}
        <div className="border-t bg-background px-4 py-3 shrink-0 space-y-2">
          {/* Row 1: External links */}
          <div className="flex gap-2">
            {location.google_maps_url && (
              <Button size="sm" variant="outline" className="flex-1" asChild>
                <a href={location.google_maps_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-3.5 mr-1.5" /> Google Maps
                </a>
              </Button>
            )}
            {location.website && (
              <Button size="sm" variant="outline" className="flex-1" asChild>
                <a href={location.website} target="_blank" rel="noopener noreferrer">
                  <Globe className="size-3.5 mr-1.5" /> Website
                </a>
              </Button>
            )}
          </div>
          {/* Row 2: Actions */}
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              <Save className="size-4 mr-1.5" /> Save
            </Button>
            <Button variant="outline" className="flex-1" onClick={onSkipped}>
              <SkipForward className="size-4 mr-1.5" /> Skip
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
            >
              <Ban className="size-4 mr-1.5" /> Reject
            </Button>
          </div>
        </div>
      </div>

      {/* No courts placed confirmation dialog */}
      <AlertDialog open={showNoCourtsDialog} onOpenChange={setShowNoCourtsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No courts placed</AlertDialogTitle>
            <AlertDialogDescription>
              You haven't placed any sport court pins. Are you sure you want to save?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { doSave(); setShowNoCourtsDialog(false); }}>
              Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Google Reviews modal */}
      <GoogleReviewsModal
        open={showReviewsModal}
        onOpenChange={setShowReviewsModal}
        locationId={locationId}
      />
    </div>
  );
}
